import { z } from "zod";
import { ScratchPadState } from "./types";

const pointSchema = z.tuple([z.number(), z.number(), z.number().optional()]);

const strokeSchema = z.object({
  tool: z.union([z.literal("pen"), z.literal("eraser")]),
  color: z.string(),
  size: z.number(),
  points: z.array(pointSchema),
});

const textItemSchema = z.object({
  id: z.string(),
  text: z.string(),
  x: z.number(),
  y: z.number(),
  w: z.number(),
});

// Uploaded images are intentionally NOT persisted. Once bytes are sent to the
// backend they're transcribed and the conversation history holds the text. The
// frontend keeps only the on-screen icon placeholder in memory.
const stateSchema = z.object({
  strokes: z.array(strokeSchema),
  textItems: z.array(textItemSchema).default([]),
  text: z.string().optional(),
});

const emptyState: ScratchPadState = {
  strokes: [],
  textItems: [],
  imageItems: [],
  text: "",
};

const storageKey = (
  sessionId?: string | null,
  slideIndex?: number | string | null,
) => `scratchpad:v1:${sessionId ?? "unknown"}:${slideIndex ?? "unknown"}`;

export const loadScratchPadState = (
  sessionId?: string | null,
  slideIndex?: number | string | null,
) => {
  try {
    const raw = localStorage.getItem(storageKey(sessionId, slideIndex));
    if (!raw) return emptyState;
    const parsed = stateSchema.safeParse(JSON.parse(raw));
    if (!parsed.success) return emptyState;
    return {
      strokes: parsed.data.strokes,
      textItems: parsed.data.textItems,
      imageItems: [],
      text: parsed.data.text,
    } as ScratchPadState;
  } catch {
    return emptyState;
  }
};

export const saveScratchPadState = (
  sessionId: string | null | undefined,
  slideIndex: number | string | null | undefined,
  state: ScratchPadState,
) => {
  const persistable = {
    strokes: state.strokes,
    textItems: state.textItems,
    text: state.text,
  };
  const parsed = stateSchema.safeParse(persistable);
  if (!parsed.success) return false;

  const key = storageKey(sessionId, slideIndex);
  // imageItems contain large base64 data URLs — excluded from localStorage since
  // scratchpad state is cleared on every lesson reload anyway (clear() fires on slideIndex change).
  const { imageItems: _ignored, ...stateWithoutImages } = parsed.data;
  try {
    localStorage.setItem(key, JSON.stringify(stateWithoutImages));
    return true;
  } catch {
    return false;
  }
};

export const removeScratchPadState = (
  sessionId?: string | null,
  slideIndex?: number | string | null,
) => {
  try {
    localStorage.removeItem(storageKey(sessionId, slideIndex));
  } catch {
    // Ignore storage errors
  }
};

export const clearScratchPadSession = (sessionId?: string | null) => {
  const prefix = `scratchpad:v1:${sessionId ?? "unknown"}:`;
  try {
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Ignore storage errors
  }
};
